/**
 * SRES · useCinemaMode hook (Iter112 · Feb 2026).
 *
 * Shared "cinema mode" state across the top BrandHeader and the
 * wizard sidebar. When enabled, BOTH the top nav bar and the 9-step
 * sidebar disappear so the user reclaims 100 % of the viewport for
 * the 3D cockpit, GIS map, block-model preview or full-screen reports.
 *
 * Storage
 * -------
 *   localStorage["sres.sidebar-hidden"]  · "1" (on) or "0" (off).
 *   Keeps compatibility with the Iter111 sidebar-only hidden state so
 *   the existing FAB + drawer keep working unchanged.
 *
 * Pub / sub
 * ---------
 *   Any setter dispatches a `window` custom event `sres:cinema-change`
 *   plus writes to localStorage. Cross-tab sync via the native
 *   `storage` event.
 *
 * Usage
 * -----
 *   const [cinema, setCinema, toggle] = useCinemaMode();
 */
import { useCallback, useEffect, useState } from "react";

export const CINEMA_LS_KEY   = "sres.sidebar-hidden";
export const CINEMA_EVENT    = "sres:cinema-change";

function _read() {
  try { return localStorage.getItem(CINEMA_LS_KEY) === "1"; }
  catch { return false; }
}

function _write(val) {
  try { localStorage.setItem(CINEMA_LS_KEY, val ? "1" : "0"); }
  catch { /* silent */ }
  try { window.dispatchEvent(new CustomEvent(CINEMA_EVENT, { detail: val })); }
  catch { /* silent */ }
}


export function useCinemaMode() {
  const [state, setState] = useState(() => _read());

  // Keep in sync with cross-component broadcasts + cross-tab writes.
  useEffect(() => {
    const onCustom = (e) => setState(!!e?.detail);
    const onStorage = (e) => {
      if (e.key === CINEMA_LS_KEY) setState(e.newValue === "1");
    };
    window.addEventListener(CINEMA_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CINEMA_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const set = useCallback((v) => {
    const next = typeof v === "function" ? v(_read()) : !!v;
    _write(next);
    setState(next);
  }, []);
  const toggle = useCallback(() => set((v) => !v), [set]);

  return [state, set, toggle];
}


/**
 * Read the current cinema flag imperatively (no re-renders). Handy
 * for the global keyboard-shortcut listener that lives outside any
 * component tree.
 */
export function getCinemaMode()  { return _read(); }
export function setCinemaMode(v) { _write(!!v); }
